import React, { FC, useState, useRef, useEffect } from 'react'
import cn from 'classnames'
import { IProductTag } from '../../types/products'
import TagEditor from './TagEditor'
import TagEditorModal from './TagEditorModal'
import TagDot from './TagDot'

interface ITagDropdownProps {
  tags: IProductTag[]
  selectedTagId?: number
  selectedPendingTag?: { name: string; color: string }
  onPendingCreate: (tag: { name: string; color: string }) => void
  onChange: (tag: IProductTag | undefined) => void
}

const TagDropdown: FC<ITagDropdownProps> = ({
  tags,
  selectedTagId,
  selectedPendingTag,
  onPendingCreate,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [showEditor, setShowEditor] = useState(false)
  const [editorInitialName, setEditorInitialName] = useState('')
  const popupRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!isOpen) return
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        !popupRef.current?.contains(target) &&
        !triggerRef.current?.contains(target)
      ) {
        setIsOpen(false)
        setSearch('')
      }
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [isOpen])

  const filtered = tags.filter((t) =>
    t.name.toLowerCase().includes(search.toLowerCase()),
  )

  const handleSelect = (tag: IProductTag | undefined) => {
    onChange(tag)
    setIsOpen(false)
    setSearch('')
  }

  const handleCreateNew = () => {
    setEditorInitialName(search)
    setShowEditor(true)
    setIsOpen(false)
    setSearch('')
  }

  const handleCreate = (
    name: string,
    color: string,
  ): Promise<{ errors?: any; result?: IProductTag }> => {
    if (tags.some((t) => t.name === name)) {
      return Promise.resolve({ errors: { name: 'already exists' } })
    }
    onPendingCreate({ name, color })
    setShowEditor(false)
    return Promise.resolve({})
  }

  const displayTag: { name: string; color: string } | undefined =
    selectedPendingTag ??
    (selectedTagId != null
      ? tags.find((t) => Number(t.id) === Number(selectedTagId))
      : undefined)

  return (
    <div className="product-tag_wrapper">
      {showEditor && (
        <TagEditorModal title="New tag" onClose={() => setShowEditor(false)}>
          <TagEditor
            mode="create"
            initialName={editorInitialName}
            onSave={handleCreate}
          />
        </TagEditorModal>
      )}
      <div className="product-info_label">TAG</div>
      {displayTag ? (
        <button
          ref={triggerRef}
          type="button"
          className="product-tag_selected-button"
          onClick={() => setIsOpen(!isOpen)}
        >
          <TagDot color={displayTag.color} />
          {displayTag.name}
        </button>
      ) : (
        <button
          ref={triggerRef}
          type="button"
          className="product-tag_add-button"
          onClick={() => setIsOpen(true)}
        >
          + Add tag
        </button>
      )}

      {isOpen && (
        <div ref={popupRef} className="product-tag_popup-outer">
          <div className="product-tag_popup">
            <div className="product-tag_popup-search">
              <input
                className="product-tag_popup-input"
                placeholder="Type to search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>
            <div className="product-tag_popup-list">
              <button
                type="button"
                className="product-tag_popup-item"
                onClick={() => handleSelect(undefined)}
              >
                <TagDot />
                No tag
              </button>
              {filtered.map((tag) => (
                <button
                  key={tag.id}
                  type="button"
                  className={cn('product-tag_popup-item', {
                    '-selected': Number(tag.id) === Number(selectedTagId),
                  })}
                  onClick={() => handleSelect(tag)}
                >
                  <TagDot color={tag.color} />
                  {tag.name}
                </button>
              ))}
              <button
                type="button"
                className="product-tag_popup-item -create"
                onClick={handleCreateNew}
              >
                + Create new tag
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TagDropdown
